import React, {
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/client';
import { TabContent, TabPane, Card, CardBody, Label } from 'reactstrap';
import CustomTabs from 'components/CustomTabs';
import Heading from 'components/Heading';
import FSBModal from 'components/common/FSBModal';
import Select from 'react-select';
import CategoryGradeDetails from 'components/Drawers/components/CategoryDetailsDrawer/components/CategoriesDetails/components/CategoryGradeDetails';
import { OFFER_AND_DEMAND, TYPE_OF_OFFER_AND_DEMAND } from 'helper/constants';
import { GRADES_REQUEST, PRODUCT_LIST } from 'pages/Products/ProductsGraphQL';
import Demands from './components/Demands';
import Offers from './components/Offers';
import LiveOfferDetails from './components/LiveOfferDetails';
import ForeignOfferDetails from './components/ForeignOfferDetails';
import ForwardOfferDetails from './components/ForwardOfferDetails';
import { GradeContext } from './GradeContext';

const ALL_OPTION_PRODUCT = { id: 'all', name: 'All' };

const MarketListing = () => {
  const location = useLocation();
  const [offerDetail, setOfferDetail] = useState('');
  const [typeDetail, setTypeDetail] = useState('offerType');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(
    location?.state?.value || OFFER_AND_DEMAND.OFFERS,
  );
  const [showGradeDetails, setShowGradeDetails] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState(null);

  // Product filter state
  const [selectedProduct, setSelectedProduct] = useState(ALL_OPTION_PRODUCT);

  // Fetch products using PRODUCT_LIST GraphQL query (same as Products page)
  const { data: productListData } = useQuery(PRODUCT_LIST, {
    variables: {
      filter: [
        {
          key: 'status',
          value: 'ACTIVE',
          type: 'STRING',
          op: 'EQ',
        },
      ],
      pagination: false,
    },
    fetchPolicy: 'network-only',
  });

  const productFilterValue = useMemo(() => {
    if (!selectedProduct?.id || selectedProduct.id === 'all') {
      return [];
    }

    return [
      {
        key: 'id',
        value: parseInt(selectedProduct.id, 10),
        setAttribute: 'grades',
        joinAttributeName: 'product',
        innerAttributeName: 'id',
        type: 'INT',
        op: 'EQ',
      },
    ];
  }, [selectedProduct?.id]);

  const products = useMemo(() => {
    const list = productListData?.productList?.productList;
    if (!Array.isArray(list)) return [];
    return list;
  }, [productListData]);

  const productOptions = useMemo(
    () => [ALL_OPTION_PRODUCT, ...products],
    [products],
  );

  const [getGradeDetails] = useLazyQuery(GRADES_REQUEST, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const fullGrade = data?.grades?.grades?.[0];
      if (fullGrade) {
        setSelectedGrade(fullGrade);
      }
    },
  });

  const openGradeDetails = useCallback((grade) => {
    setSelectedGrade(grade);
    setShowGradeDetails(true);
    getGradeDetails({
      variables: {
        filter: [
          {
            key: 'id',
            value: parseInt(grade.id, 10),
            type: 'INT',
            op: 'EQ',
          },
        ],
        pageNo: 1,
        pageSize: 10,
      },
    });
  }, [getGradeDetails]);
  const closeGradeDetails = () => setShowGradeDetails(false);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };
  const drawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleViewDetail = (row, type) => {
    setTypeDetail(type);
    drawerToggle();
    if (row[type] === TYPE_OF_OFFER_AND_DEMAND.LIVE) {
      setOfferDetail(<LiveOfferDetails row={row} type={type} />);
    } else if (row[type] === TYPE_OF_OFFER_AND_DEMAND.FOREIGN) {
      setOfferDetail(<ForeignOfferDetails row={row} type={type} />);
    } else if (row[type] === TYPE_OF_OFFER_AND_DEMAND.FORWARD) {
      setOfferDetail(<ForwardOfferDetails row={row} type={type} />);
    }
  };

  return (
    <GradeContext.Provider value={openGradeDetails}>
      <div className="market-listing-page">
        <div className="mb-3 top-head pb-1 d-flex justify-content-between">
          <Heading title="Market Listing" />
        </div>

        <FSBModal
          title={`${typeDetail === 'offerType' ? 'Offer' : 'Demand'} Details`}
          open={isDrawerOpen}
          toggleAlert={drawerToggle}
          onClose={drawerToggle}
          message=""
          modalClass="right-drawer right-drawer-details"
        >
          {offerDetail}
        </FSBModal>

        <FSBModal
          size="xl"
          title="Category Grade Details"
          open={showGradeDetails}
          onClose={closeGradeDetails}
          toggleAlert={closeGradeDetails}
          modalClass="right-drawer right-drawer-details"
          closeButtonLabel=""
          confirmButtonLabel=""
          message=""
        >
          <CategoryGradeDetails categoryGradeDetails={selectedGrade} />
        </FSBModal>

        <Card className="border-0 card-box page-card-box">
          <CardBody>
            <div className="market-listing-controls d-flex align-items-center justify-content-between mb-2">
              <CustomTabs
                tabs={[OFFER_AND_DEMAND.OFFERS, OFFER_AND_DEMAND.DEMANDS]}
                selectedTab={activeTab}
                onTabChange={toggle}
                className="mb-0 border-0"
              />
              <div className="market-listing-product-filter filter-row-ui">
                <div className="form-group mb-0">
                  <Label>Product</Label>
                  <Select
                    name="Product"
                    options={productOptions}
                    getOptionLabel={(product) => product?.name || 'All'}
                    getOptionValue={(product) => product?.id}
                    placeholder="All"
                    classNamePrefix="react-select"
                    value={selectedProduct}
                    onChange={(value) =>
                      setSelectedProduct(value || ALL_OPTION_PRODUCT)
                    }
                  />
                </div>
              </div>
            </div>
            <TabContent activeTab={activeTab}>
              <TabPane tabId={OFFER_AND_DEMAND.OFFERS}>
                {activeTab === OFFER_AND_DEMAND.OFFERS && (
                  <Offers
                    handleViewDetail={handleViewDetail}
                    productFilter={productFilterValue}
                    selectedProductName={selectedProduct?.id === 'all' ? null : selectedProduct?.name}
                  />
                )}
              </TabPane>
              <TabPane tabId={OFFER_AND_DEMAND.DEMANDS}>
                {activeTab === OFFER_AND_DEMAND.DEMANDS && (
                  <Demands
                    handleViewDetail={handleViewDetail}
                    productFilter={productFilterValue}
                    selectedProductName={selectedProduct?.id === 'all' ? null : selectedProduct?.name}
                  />
                )}
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </div>
    </GradeContext.Provider>
  );
};

export default MarketListing;
